import FileUploadForm from "components/PLCTABS/CompanyOfficialsTab/FileUploadForm";
import Checkbox from "components/lib/Shared/Checkbox";
import DatePickerField from "components/lib/Shared/DatePickerField";
import ReactSelectWithFormik from "components/lib/Shared/DropdownSelect";
import { format, lastDayOfMonth, parse } from "date-fns";
import { Field } from "formik";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

const FinancialStatements = ({
  field,
  form: {
    values,
    errors,
    touched,
    setFieldValue,
    validateField,
    setFieldTouched,
    isSubmitting,
  },
  filteredFinancialYearOptions,
}) => {
  const { company } = useSelector((state) => state.companySlice);

  // State to track availability for each financial statement
  const [availability, setAvailability] = useState(
    values?.financialStatements?.statements?.map((statement) => ({
      isAvailable: !statement.isNotAvailable,
      isNotAvailable: statement.isNotAvailable || false,
    })) || []
  );

  const documentData = [
    {
      label: "Financial Statements",
      name: "financialStatements",
      accept: "application/pdf",
    },
  ];

  // Set touched for lastFinancialStatementYear and fileUploads
  useEffect(() => {
    if (values?.financialStatements?.lastFinancialStatementYear) {
      setFieldTouched("financialStatements.lastFinancialStatementYear", true);
    }
    values?.financialStatements?.statements?.forEach((_, index) => {
      const fileUploadsName = `financialStatements.statements[${index}].fileUploads`;
      const fileUploads =
        values?.financialStatements?.statements[index]?.fileUploads;
      if (
        fileUploads?.financialStatements &&
        !values?.financialStatements?.statements[index]?.isNotAvailable
      ) {
        setFieldTouched(fileUploadsName, true);
        validateField(fileUploadsName);
      }
    });
  }, [
    values?.financialStatements?.lastFinancialStatementYear,
    values?.financialStatements?.statements,
    setFieldTouched,
    validateField,
  ]);

  const isYearDisabled = (index) => {
    if (availability.every((a) => a.isNotAvailable)) {
      return false;
    }

    // Find the last "Yes" year (isAvailable: true)
    const lastYesIndex = availability.findLastIndex((a) => a.isAvailable);

    if (lastYesIndex === -1) {
      return false;
    }

    // Years before the last "Yes" are disabled
    if (index < lastYesIndex) {
      return true;
    }

    // The year immediately after the last "Yes" is enabled
    if (index === lastYesIndex + 1) {
      return false;
    }

    // Years beyond the immediate next are disabled
    if (index > lastYesIndex + 1) {
      return true;
    }

    // The last "Yes" year itself is enabled (can be toggled to "No")
    return false;
  };

  const handleAvailabilityChange = (index, isNotAvailable) => {
    const newAvailability = [...availability];
    const newStatements = [...(values?.financialStatements?.statements || [])];

    if (!isNotAvailable) {
      // USER SELECTED "YES" → Only this specific year becomes Available
      newAvailability[index] = { isAvailable: true, isNotAvailable: false };

      newStatements[index] = {
        ...newStatements[index],
        isNotAvailable: false,
        yearsOfStatement: format(
          new Date(`${newStatements[index]?.year}`),
          "yyyy"
        ),
        // Preserve existing files/dates if present
        fileUploads: newStatements[index].fileUploads || {
          financialStatements: null,
        },
        dateOfReturn: newStatements[index].dateOfReturn || "",
        boardApprovalDate: newStatements[index].boardApprovalDate || "",
        auditorApprovalDate: newStatements[index].auditorApprovalDate || "",
      };

      // Reset all years after this one to "No"
      for (let i = index + 1; i < newAvailability.length; i++) {
        newAvailability[i] = { isAvailable: false, isNotAvailable: true };

        newStatements[i] = {
          ...newStatements[i],
          isNotAvailable: true,
          yearsOfStatement: "",
          fileUploads: { financialStatements: null },
          boardApprovalDate: "",
          auditorApprovalDate: "",
        };
      }
    } else {
      // USER SELECTED "NO" → This year becomes Not Available
      newAvailability[index] = { isAvailable: false, isNotAvailable: true };

      newStatements[index] = {
        ...newStatements[index],
        isNotAvailable: true,
        yearsOfStatement: "",
        fileUploads: { financialStatements: null },
        boardApprovalDate: "",
        auditorApprovalDate: "",
      };

      // If we're turning the last "Yes" to "No", enable all years after it
      const lastYesIndex = newAvailability.findLastIndex((a) => a.isAvailable);
      if (index === lastYesIndex) {
        // After turning this "Yes" to "No", enable all subsequent years
        for (let i = index + 1; i < newAvailability.length; i++) {
          // But don't change their actual values, just enable them
          // They will remain "No" until user explicitly changes them
        }
      }
    }

    setAvailability(newAvailability);
    setFieldValue("financialStatements.statements", newStatements);
  };

  // Helper function to get minDate and maxDate for date pickers
  const getDateConstraints = (index) => {
    const boardApprovalDate =
      values?.financialStatements?.statements[index]?.boardApprovalDate;
    const auditorApprovalDate =
      values?.financialStatements?.statements[index]?.auditorApprovalDate;

    let boardMinDate = null;
    let boardMaxDate = null;
    let auditorMinDate = null;
    let auditorMaxDate = null;

    if (auditorApprovalDate) {
      // If auditor date is selected, board date must be on or before it
      boardMaxDate = parse(auditorApprovalDate, "yyyy/MM/dd", new Date());
    }
    if (boardApprovalDate) {
      // If board date is selected, auditor date must be on or after it
      auditorMinDate = parse(boardApprovalDate, "yyyy/MM/dd", new Date());
    }

    return {
      boardMinDate,
      boardMaxDate,
      auditorMinDate,
      auditorMaxDate,
      boardApprovalDate,
      auditorApprovalDate,
    };
  };

  return (
    <div>
      <div className="flex items-baseline justify-between m-4">
        <p
          htmlFor={field.name}
          className="block  text-sm text-primary font-bold min-w-[13rem]  w-full whitespace-normal"
        >
          Select the years for which you have financial statement(s) filed
        </p>
      </div>
      {filteredFinancialYearOptions?.map((option, index) => {
        // Safety check to ensure statement exists
        if (!values?.financialStatements?.statements?.[index]) {
          return <div>Loading...</div>; // or loading state
        }

        const fileUploadsName = `financialStatements.statements[${index}].fileUploads`;
        const boardApprovalDateName = `financialStatements.statements[${index}].boardApprovalDate`;
        const auditorApprovalDateName = `financialStatements.statements[${index}].auditorApprovalDate`;

        const {
          boardMinDate,
          boardMaxDate,
          auditorMinDate,
          auditorMaxDate,
          boardApprovalDate,
          auditorApprovalDate,
        } = getDateConstraints(index);

        // Check if this year is disabled
        const disabled = isYearDisabled(index);

        return (
          <div
            key={option.value}
            className="shadow-sm sm:rounded-md sm:overflow-hidden px-4 py-6 space-y-4 bg-gray-50 sm:p-6 mt-4"
          >
            <div className=" mb-4">
              <h2 className="text-4xl font-bold text-gray-800">
                {option.label}
              </h2>
              <p className="text-gray-800 font-bold">Financial Statements</p>
            </div>
            <div className="flex justify-between">
              <div className="w-[85%]">
                <Field
                  name={fileUploadsName}
                  component={FileUploadForm}
                  documentData={documentData}
                  setFieldValue={(name, value) => {
                    setFieldValue(name, value);
                    setFieldTouched(name, true);
                    validateField(name);
                  }}
                  setFieldTouched={(name) => {
                    setFieldTouched(name, true);
                    validateField(name);
                  }}
                  disabled={availability[index]?.isNotAvailable || disabled}
                  showRequiredAsterisk
                  showModeType={values?.financialStatements?.statements?.find(
                    (statement, indexValue) =>
                      indexValue === index &&
                      statement?.fileUploads?.financialStatements
                  )}
                />
                <ValidationError
                  errorMessage={
                    errors?.financialStatements?.statements?.[index]
                      ?.fileUploads
                  }
                  isTouched={
                    touched?.financialStatements?.statements?.[index]
                      ?.fileUploads
                  }
                />
              </div>
              <div className="flex flex-col items-start justify-start">
                <p className="text-green-focused font-medium text-sm">
                  Available
                </p>
                <div className="flex gap-2">
                  <Checkbox
                    name={`availability[${index}].isAvailable`}
                    label="Yes"
                    checked={availability[index]?.isAvailable}
                    onChange={() => handleAvailabilityChange(index, false)}
                    disabled={disabled && !availability[index]?.isAvailable}
                  />
                  <Checkbox
                    name={`availability[${index}].isNotAvailable`}
                    label="No"
                    checked={availability[index]?.isNotAvailable}
                    onChange={() => handleAvailabilityChange(index, true)}
                    disabled={disabled && availability[index]?.isAvailable}
                  />
                </div>
              </div>
            </div>

            <div>
              <DatePickerField
                label="Board Approval Date"
                onChange={(date) => {
                  if (!date) {
                    setFieldValue(boardApprovalDateName, "");
                    setFieldTouched(boardApprovalDateName, true);
                    validateField(boardApprovalDateName);
                    return;
                  }
                  if (isNaN(date.getTime())) {
                    console.error("Invalid date:", date);
                    return;
                  }
                  const formattedDate = format(date, "yyyy/MM/dd");
                  setFieldValue(boardApprovalDateName, formattedDate);
                  setFieldTouched(boardApprovalDateName, true);
                  validateField(boardApprovalDateName);

                  // If auditor date is set and is before the new board date, clear it
                  if (
                    auditorApprovalDate &&
                    parse(auditorApprovalDate, "yyyy/MM/dd", new Date()) < date
                  ) {
                    setFieldValue(auditorApprovalDateName, "");
                    setFieldTouched(auditorApprovalDateName, true);
                    validateField(auditorApprovalDateName);
                  }
                }}
                selected={
                  values?.financialStatements?.statements[index]
                    ?.boardApprovalDate
                    ? new Date(
                        values?.financialStatements?.statements[
                          index
                        ].boardApprovalDate
                      )
                    : null
                }
                name={boardApprovalDateName}
                placeholderText="Enter Board Approval Date"
                errorPath={boardApprovalDateName}
                minDate={boardMinDate}
                maxDate={boardMaxDate}
                disabled={
                  isSubmitting ||
                  availability[index]?.isNotAvailable ||
                  disabled
                }
                showRequiredAsterisk
              />
              <ValidationError
                errorMessage={
                  errors?.financialStatements?.statements?.[index]
                    ?.boardApprovalDate
                }
                isTouched={
                  touched?.financialStatements?.statements?.[index]
                    ?.boardApprovalDate
                }
              />
            </div>
            <div>
              <DatePickerField
                label="Auditor Approval Date"
                onChange={(date) => {
                  if (!date) {
                    setFieldValue(auditorApprovalDateName, "");
                    setFieldTouched(auditorApprovalDateName, true);
                    validateField(auditorApprovalDateName);
                    return;
                  }
                  if (isNaN(date.getTime())) {
                    console.error("Invalid date:", date);
                    return;
                  }
                  const formattedDate = format(date, "yyyy/MM/dd");
                  setFieldValue(auditorApprovalDateName, formattedDate);
                  setFieldTouched(auditorApprovalDateName, true);
                  validateField(auditorApprovalDateName);

                  // If board date is set and is after the new auditor date, clear it
                  if (
                    boardApprovalDate &&
                    parse(boardApprovalDate, "yyyy/MM/dd", new Date()) > date
                  ) {
                    setFieldValue(boardApprovalDateName, "");
                    setFieldTouched(boardApprovalDateName, true);
                    validateField(boardApprovalDateName);
                  }
                }}
                selected={
                  values?.financialStatements?.statements[index]
                    ?.auditorApprovalDate
                    ? new Date(
                        values?.financialStatements?.statements[
                          index
                        ].auditorApprovalDate
                      )
                    : null
                }
                name={auditorApprovalDateName}
                placeholderText="Enter Auditor Approval Date"
                errorPath={auditorApprovalDateName}
                minDate={auditorMinDate}
                maxDate={auditorMaxDate}
                disabled={
                  isSubmitting ||
                  availability[index]?.isNotAvailable ||
                  disabled
                }
                showRequiredAsterisk
              />
              <ValidationError
                errorMessage={
                  errors?.financialStatements?.statements?.[index]
                    ?.auditorApprovalDate
                }
                isTouched={
                  touched?.financialStatements?.statements?.[index]
                    ?.auditorApprovalDate
                }
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FinancialStatements;

const ValidationError = ({ errorMessage, isTouched }) => {
  if (!errorMessage || !isTouched) return null;

  return (
    <>
      <div className="text-red-500 flex items-center py-2">
        <svg
          aria-hidden="true"
          className="flex-shrink-0 inline w-5 h-5 mr-1"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
            clipRule="evenodd"
          ></path>
        </svg>

        <p className="text-sm">{errorMessage}</p>
      </div>
    </>
  );
};
